import { CsvExportButton, useCsvExport } from '../../components/CsvExport';
import { projectDriveLabels } from '../../../shared/drives';
// Projects per mockups/03-projects.html: phase shelves (Onboarding …
// On hold) with drag-and-drop between phases, recently-delivered rows with
// show-all, Active/Delivered/All tabs, slim vitals, and the new-project
// modal (09-new-project). Project numbers ride top-right on cards.

import { useViewState } from '../../lib/useViewState';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { api, ApiError } from '../../lib/api';
import { reportError } from '../../lib/errorEvents';
import { NewTaskModal } from '../Tasks';
import type { ProjectOption } from '../../components/TaskRow';
import { useIsMobile } from '../../lib/useIsMobile';
import { SwipeRow } from '../../components/SwipeRow';
import { PageMenu, type PageMenuItem } from '../../components/PageMenu';
import { PickerSheet } from '../../components/PickerSheet';
import { SearchIcon, FilterIcon, DotsIcon } from '../../components/icons';
import { ProjectListFilterSheet } from '../../components/projectListFilters';
import { projectListMeta, projectListVitals, projectListMatches, projectListDefaults, projectListSaved, projectListFromSaved, type ProjectListFilters } from '../../lib/projectListMeta';
import { paramsEqual } from '../../../shared/savedViews';
import type { SavedViewItem } from '../../components/ViewChips';
import { ViewChips } from '../../components/ViewChips';
import { calendarDaysBetween, melbourneDate, melbourneToday } from '../../../shared/dates';
import {
  PROJECT_PHASES,
  PHASE_LABELS,
  PHASE_PILL_LABELS,
  PHASE_PROGRESS,
  type ProjectPhase,
} from '../../../shared/types';
import {
  BrandBadge,
  FilterBar,
  formatDayMonth,
  formatProjectNumber,
  formatWeekdayDate,
  PageHeader,
  Pill,
  SearchBox,
  SkeletonRows,
  SortButton,
  Tabs,
  VS,
  VitalsSlim,
  type SortValue,
} from '../../components/primitives';

import { ACTIVE_PHASES, SHELVES } from './constants';
import { addDaysIso, cleanupOutstanding, compareProjects, defaultSortFor, parseSort, pillTone, serializeSort, SORT_OPTIONS, waitingStatus } from './helpers';
import { NewProjectModal } from './NewProjectModal';
import { CloseProjectModal } from '../../components/CloseProjectModal';
import { NumberedTrashProjectRow } from './NumberedTrashProjectRow';
import { PhaseShelf, ProjectCardBody } from './ProjectCards';
import type { CardProject, NumberedTrashProject, View } from './types';
import { projectsViewCodec, projectViewChange } from './viewState';

export default function Projects() {
  const { initial, publish } = useViewState(projectsViewCodec, state => {
    setView(state.view);
    setSort(parseSort(state.sort ?? undefined) ?? defaultSortFor(state.view));
    setQuery(state.query);
    setFilters({ client: state.client, video_type: state.video_type, archive: state.archive });
    setShowVoided(state.showVoided);
    setShowNumberedTrash(state.showNumberedTrash);
    setPhase(state.phase);
  });
  const isMobile = useIsMobile();
  const [showFilters, setShowFilters] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [rowMenuId, setRowMenuId] = useState<string | null>(null);
  const [phaseId, setPhaseId] = useState<string | null>(null);
  const [taskProjectId, setTaskProjectId] = useState<string | null>(null);
  const [phaseBusy, setPhaseBusy] = useState(false);
  const [savedViews, setSavedViews] = useState<SavedViewItem[]>([]);
  const [draft, setDraft] = useState<ProjectListFilters>(projectListDefaults);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listSeq = useRef(0);
  const [projects, setProjects] = useState<CardProject[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState<View>(() => initial.view);
  const [sort, setSort] = useState<SortValue>(() => parseSort(initial.sort ?? undefined) ?? defaultSortFor(initial.view));
  const [query, setQuery] = useState(() => initial.query);
  const [filters, setFilters] = useState<Record<string, string | null>>(() => ({
    client: initial.client,
    video_type: initial.video_type,
    archive: initial.archive,
  }));
  const [showNew, setShowNew] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overShelf, setOverShelf] = useState<ProjectPhase | null>(null);
  const [numberedTrash, setNumberedTrash] = useState<NumberedTrashProject[]>([]);
  const [showNumberedTrash, setShowNumberedTrash] = useState(() => initial.showNumberedTrash);
  const [showVoided, setShowVoided] = useState(() => initial.showVoided);
  const [waitingActionId, setWaitingActionId] = useState<string | null>(null);
  const [waitingNote, setWaitingNote] = useState<{ id: string; text: string } | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [phase, setPhase] = useState<string | null>(() => initial.phase);
  const phaseFilter = phase as ProjectPhase | null;
  const today = melbourneToday();

  useEffect(() => {
    publish({
      view,
      query,
      sort: serializeSort(sort) === serializeSort(defaultSortFor(view)) ? null : serializeSort(sort),
      client: filters.client,
      video_type: filters.video_type,
      archive: filters.archive,
      showVoided,
      showNumberedTrash,
      phase: phaseFilter,
    });
  }, [publish, view, query, sort, filters.client, filters.video_type, filters.archive, showVoided, showNumberedTrash, phaseFilter]);

  // Saved views (phase 24): exactly the tab/filter/search state this page
  // already holds — applying a view must round-trip identically.
  // The sort only rides along when it differs from the tab's default:
  // paramsEqual (shared/savedViews.ts) is exact serialised equality, so an
  // unconditional key would stop every already-saved view from matching.
  const sortIsDefault = serializeSort(sort) === serializeSort(defaultSortFor(view));
  const viewState = {
    view,
    query,
    ...filters,
    phase: phaseFilter,
    sort: sortIsDefault ? null : serializeSort(sort),
  };
  const isDefaultView =
    view === 'active' && sortIsDefault && !query && !filters.client && !filters.video_type && !filters.archive && !phaseFilter;
  const clearPhaseFilter = () => setPhase(null);
  /** Switching tab — from the tabs or the "Show all delivered" link — resets
      the sort to that tab's default. Filters and search never touch it. */
  const changeView = (nextView: View) => {
    const next = projectViewChange(nextView);
    setPhase(next.phase);
    setView(next.view);
    setSort(parseSort(next.sort ?? undefined) ?? defaultSortFor(next.view));
  };
  const applyView = (p: Record<string, string>) => {
    clearPhaseFilter();
    const nextView: View = p.view === 'delivered' || p.view === 'all' || p.view === 'clean_up' ? p.view : 'active';
    setView(nextView);
    setSort(parseSort(p.sort) ?? defaultSortFor(nextView));
    setQuery(p.query ?? '');
    setFilters({
      client: p.client ?? null,
      video_type: p.video_type ?? null,
      archive: p.archive ?? null,
    });
  };

  const fetchProjects = useCallback(() => {
    const seq = ++listSeq.current;
    api<{ projects: CardProject[] }>('/api/admin/projects')
      .then((d) => { if (seq === listSeq.current) setProjects(d.projects); })
      .catch(() => {})
      .finally(() => { if (seq === listSeq.current) setLoaded(true); });
    api<{ projects: NumberedTrashProject[] }>('/api/admin/projects/numbered-trash')
      .then((d) => { if (seq === listSeq.current) setNumberedTrash(d.projects); })
      .catch(() => {});
  }, []);
  const refresh = useCallback(() => {
    if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
    mutatedTimer.current = null;
    fetchProjects();
  }, [fetchProjects]);
  useEffect(() => {
    fetchProjects();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchProjects, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      ++listSeq.current;
    };
  }, [fetchProjects]);
  const loadViews = useCallback(() => {
    api<{ views: SavedViewItem[] }>('/api/admin/views?page=projects')
      .then(d => setSavedViews(d.views)).catch(error => reportError(error, 'Could not load saved views.'));
  }, []);
  useEffect(() => { if (isMobile) loadViews(); }, [isMobile, loadViews]);

  const sendWaitingNow = async (projectId: string) => {
    setWaitingActionId(projectId);
    setWaitingNote(null);
    try {
      await api(`/api/admin/projects/${projectId}/waiting-nudge-now`, { method: 'POST' });
      setWaitingNote({ id: projectId, text: 'Sent!' });
      refresh();
    } catch (err) {
      setWaitingNote({ id: projectId, text: err instanceof ApiError ? err.message : 'Failed to send.' });
    } finally {
      setWaitingActionId(null);
      setTimeout(() => setWaitingNote(null), 6000);
    }
  };

  const snoozeWaiting = async (projectId: string) => {
    setWaitingActionId(projectId);
    await api(`/api/admin/projects/${projectId}`, {
      method: 'PATCH',
      json: { waiting_snoozed_until: addDaysIso(today, 7) },
    }).catch((error) => reportError(error, 'Could not snooze the client wait.'));
    refresh();
    setWaitingActionId(null);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const filtered = useMemo(() => {
    let list = projects.filter((p) => showVoided || p.voided_at === null);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) => [p.name, p.client_name].some((s) => s.toLowerCase().includes(q)));
    }
    if (filters.client) list = list.filter((p) => p.client_name === filters.client);
    if (filters.video_type) list = list.filter((p) => p.video_type === filters.video_type);
    if (filters.archive)
      list = list.filter(
        (p) => projectDriveLabels(p).includes(filters.archive!),
      );
    if (phaseFilter) list = list.filter((p) => p.phase === phaseFilter);
    return list;
  }, [projects, query, filters, showVoided, phaseFilter]);

  // Filter options come from the data itself (Henry's ask: client, video
  // type, archive drive — handy for "what's on that drive").
  const filterFields = useMemo(() => {
    const distinct = (vals: (string | null)[]) =>
      [...new Set(vals.filter((v): v is string => !!v))].sort();
    return [
      {
        key: 'client',
        label: 'Client',
        items: distinct(projects.map((p) => p.client_name)).map((v) => ({ value: v, label: v })),
        searchable: true,
      },
      {
        key: 'video_type',
        label: 'Video type',
        items: distinct(projects.map((p) => p.video_type)).map((v) => ({ value: v, label: v })),
        searchable: true,
      },
      {
        key: 'archive',
        label: 'Archive drive',
        items: distinct(
          projects.flatMap(projectDriveLabels),
        ).map((v) => ({ value: v, label: v })),
        searchable: true,
      },
    ];
  }, [projects]);

  const byPhase = useMemo(() => {
    const map = new Map<ProjectPhase, CardProject[]>();
    for (const p of filtered) {
      const list = map.get(p.phase) ?? [];
      list.push(p);
      map.set(p.phase, list);
    }
    // Shelves follow the Sort button too (Henry's call), defaulting to the
    // nearest key date first.
    const compare = compareProjects(sort);
    for (const list of map.values()) list.sort(compare);
    return map;
  }, [filtered, sort]);

  // Slim vitals over all projects.
  const nonVoidedProjects = projects.filter((p) => p.voided_at === null);
  const active = nonVoidedProjects.filter((p) => ACTIVE_PHASES.includes(p.phase));
  const activeCount = phaseFilter ? filtered.length : active.length;
  const nextShoot = active
    .map((p) => p.shoot_date)
    .filter((d): d is string => d !== null && d >= today)
    .sort()[0];
  const openTasks = active.reduce((s, p) => s + p.open_tasks, 0);
  const fyStart =
    today >= `${today.slice(0, 4)}-07-01`
      ? `${today.slice(0, 4)}-07-01`
      : `${Number(today.slice(0, 4)) - 1}-07-01`;
  const delivered = projects
    .filter((p) => showVoided || p.voided_at === null)
    .filter((p) => p.phase === 'delivered')
    .sort(compareProjects({ key: 'delivered', dir: 'desc' }));
  // The Recently-delivered shelf shows the three newest deliveries, ordered
  // by the active sort like every other Active shelf.
  const recentDelivered = delivered.slice(0, 3).sort(compareProjects(sort));
  const deliveredFY = nonVoidedProjects.filter(
    (p) => p.phase === 'delivered' && p.delivered_date && p.delivered_date >= fyStart,
  ).length;

  // Clean up (feedback round three): delivered work still being tidied. Not
  // active, so it has its own tab; the toolbar filters don't apply to it.
  const cleanupRows = nonVoidedProjects
    .filter((p) => p.phase === 'clean_up')
    .sort(compareProjects(view === 'clean_up' ? sort : defaultSortFor('clean_up')));
  const cleanupNeedBackup = cleanupRows.filter((p) => cleanupOutstanding(p).includes('backup')).length;
  const cleanupNeedSelects = cleanupRows.filter((p) => cleanupOutstanding(p).includes('selects')).length;
  const closingProject = projects.find((p) => p.id === closingId);
  const cleanupReason = (p: CardProject) => {
    const out = cleanupOutstanding(p);
    const readyLabel = p.manual_backup_label
      ? `Backed up · ${p.manual_backup_label}`
      : p.has_manual_backup ? 'Backed up · ready to close' : 'No footage · ready to close';
    return out.length ? (
      <span className="cleanup-reason">{out.map((k) => (k === 'selects' ? 'Export selects' : 'Back up footage')).join(' · ')}</span>
    ) : (
      <span className="cleanup-reason ok">✓ {readyLabel}</span>
    );
  };
  const closeModal = closingProject && (
    <CloseProjectModal
      project={closingProject}
      onClose={() => setClosingId(null)}
      onClosed={() => { setClosingId(null); refresh(); }}
    />
  );

  const onDragStart = (e: DragStartEvent) => setDraggingId(String(e.active.id));
  const onDragOver = (e: DragOverEvent) => setOverShelf((e.over?.id as ProjectPhase) ?? null);
  const onDragEnd = async (e: DragEndEvent) => {
    const id = String(e.active.id);
    const target = e.over?.id as ProjectPhase | undefined;
    setDraggingId(null);
    setOverShelf(null);
    const project = projects.find((p) => p.id === id);
    if (!project || project.voided_at !== null || !target || project.phase === target) return;
    const prev = projects;
    setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, phase: target } : p)));
    try {
      await api(`/api/admin/projects/${id}`, { method: 'PATCH', json: { phase: target } });
      refresh();
    } catch {
      setProjects(prev);
    }
  };

  const draggingProject = draggingId ? projects.find((p) => p.id === draggingId) : null;

  const listRows =
    view === 'delivered'
      ? filtered.filter((p) => p.phase === 'delivered').sort(compareProjects(sort))
      : view === 'all'
        ? [...filtered].sort(compareProjects(sort))
        : [];

  const csvExport = useCsvExport('projects', { view, query, ...filters, phase: phaseFilter, sort: serializeSort(sort), showVoided }, {
    count: view === 'active' ? filtered.filter(p => ACTIVE_PHASES.includes(p.phase)).length : listRows.length,
  });
  const mobileState: ProjectListFilters = { view, query, phase: phaseFilter, client: filters.client,
    video_type: filters.video_type, archive: filters.archive, sort: serializeSort(sort), showVoided };
  const applyMobile = (state: ProjectListFilters) => {
    setView(state.view); setQuery(state.query); setSort(parseSort(state.sort) ?? defaultSortFor(state.view));
    setFilters({ client: state.client, video_type: state.video_type, archive: state.archive });
    setShowVoided(state.showVoided);
    setPhase(state.view === 'active' && state.phase && ACTIVE_PHASES.includes(state.phase as ProjectPhase) ? state.phase : null);
  };
  const openFilters = () => { setDraft(mobileState); setShowFilters(true); };
  const phaseProject = projects.find(p => p.id === phaseId);
  const menuProject = projects.find(p => p.id === rowMenuId);
  const taskProjects: ProjectOption[] = projects.filter(p => p.voided_at === null).map(p => ({
    id: p.id, name: p.name, phase: p.phase,
    sub: [p.client_name, formatProjectNumber(p.project_number)].filter(Boolean).join(' · '),
    group: p.phase === 'delivered' || p.phase === 'clean_up' ? 'Recently delivered' : 'Active',
  }));
  const changePhase = async (phase: ProjectPhase) => {
    if (!phaseProject || phaseBusy || phaseProject.voided_at !== null || phaseProject.phase === phase) return;
    if (phase === 'closed' && ['delivered', 'clean_up'].includes(phaseProject.phase)) {
      setPhaseId(null);
      setClosingId(phaseProject.id);
      return;
    }
    setPhaseBusy(true);
    try {
      await api(`/api/admin/projects/${phaseProject.id}`, { method: 'PATCH', json: { phase } });
      refresh();
    } catch (error) { reportError(error, 'Could not change project phase.'); }
    finally { setPhaseBusy(false); }
  };
  const rowItems: PageMenuItem[] = menuProject ? [
    { key: 'open', label: 'Open project', onSelect: () => navigate(`/projects/${menuProject.id}`) },
    { key: 'phase', label: 'Phase', sublabel: menuProject.voided_at !== null ? 'Unavailable on voided projects' : PHASE_LABELS[menuProject.phase], disabled: phaseBusy || menuProject.voided_at !== null, onSelect: () => setPhaseId(menuProject.id) },
    { key: 'task', label: '+ Task', disabled: menuProject.voided_at !== null, onSelect: () => setTaskProjectId(menuProject.id) },
    ...(menuProject.waiting_on_client_since !== null ? [
      { key: 'send', label: 'Send now', sublabel: waitingStatus(menuProject, today), disabled: waitingActionId !== null || menuProject.voided_at !== null, onSelect: () => { void sendWaitingNow(menuProject.id); } },
      { key: 'snooze', label: '+1w', sublabel: 'Snooze the client nudge for one week', disabled: waitingActionId !== null || menuProject.voided_at !== null, onSelect: () => { void snoozeWaiting(menuProject.id); } },
    ] : []),
  ] : [];
  const mobileRow = (p: CardProject, compact = false) => {
    const meta = projectListMeta(p, today);
    return <SwipeRow key={p.id} className="project-list-swipe"
      rightAction={p.voided_at === null && !phaseBusy ? { label: 'Phase', icon: '↔', tone: 'gold', onSelect: () => setPhaseId(p.id) } : undefined}
      leftActions={[...(p.voided_at === null ? [{ label: '+ Task', icon: '+', tone: 'gold' as const, onSelect: () => setTaskProjectId(p.id) }] : []), { label: 'More', icon: '···', tone: 'grey', onSelect: () => setRowMenuId(p.id) }]}>
      <div className={`project-list-row${compact ? ' compact' : ' prow'}`}>
        <button type="button" className="project-list-open" onClick={() => navigate(`/projects/${p.id}`)} aria-label={`Open ${p.name}`}>
          <span className="prow-main">
            <span className="prow-title">{p.name} {p.voided_at !== null && <Pill tone="danger">Voided</Pill>}</span>
            {compact ? <span className="prow-meta">{p.client_name} <BrandBadge brand={p.brand} /> {view === 'all' && <Pill tone={pillTone(p.phase)}>{PHASE_PILL_LABELS[p.phase]}</Pill>}</span>
              : <><span className="prow-meta" title={meta.text}>{p.client_name && <><span className="prow-client">{p.client_name}</span><span className="prow-sep">·</span></>}<span className={meta.hot ? 'gold' : undefined}>{meta.dateText}</span></span>
                <span className="prow-next">{p.waiting_on_client_since !== null ? <Pill tone="gold">⏳ waiting on client · {Math.max(0, calendarDaysBetween(melbourneDate(p.waiting_on_client_since), today))}d</Pill>
                  : meta.nextTask ? <><span className="prow-arrow">→</span><span className={meta.overdue ? 'danger' : undefined}>{meta.nextTask}</span></> : <span className="prow-none">{p.phase === 'on_hold' ? 'Paused' : 'No open tasks'}</span>}</span></>}
          </span>
          <span className="prow-right"><span>{p.project_number !== null ? formatProjectNumber(p.project_number) : '—'}</span>
            {compact ? <span>{formatDayMonth(p.phase === 'delivered' || p.phase === 'clean_up' ? p.delivered_date : p.due_date)}</span> : <><span className="prow-count">{meta.open}</span><span className="prow-bar">{PHASE_PROGRESS[p.phase] !== null && <i style={{ width: `${PHASE_PROGRESS[p.phase]! * 100}%` }} />}</span></>}
          </span>
        </button>
        <button type="button" className="project-list-more" aria-label={`Actions for ${p.name}`} onClick={() => setRowMenuId(p.id)}>···</button>
      </div>
      {waitingNote?.id === p.id && <div className="project-list-notice" role="status">{waitingNote.text}</div>}
    </SwipeRow>;
  };
  if (isMobile) {
    const vitals = projectListVitals(nextShoot, openTasks, deliveredFY);
    // Recently delivered observes the same filters, but remains a separate
    // preview: the filter footer counts projects in the selected tab.
    const recentFor = (state: ProjectListFilters) => state.phase ? [] : projects
      .filter(p => projectListMatches(p, { ...state, view: 'delivered' }))
      .sort(compareProjects({ key: 'delivered', dir: 'desc' })).slice(0, 3)
      .sort(compareProjects(parseSort(state.sort) ?? defaultSortFor(state.view)));
    const recent = recentFor(mobileState);
    const draftCount = projects.filter(p => projectListMatches(p, draft)).length;
    return <>
      <PageHeader title="Projects" count={showVoided ? projects.length : nonVoidedProjects.length} mobileActions={[
        { key: 'search', label: 'Search', icon: <SearchIcon />, onPress: () => window.dispatchEvent(new Event('ss:open-search')) },
        { key: 'filter', label: 'Filter & sort', icon: <FilterIcon />, onPress: openFilters },
        { key: 'menu', label: 'Projects menu', icon: <DotsIcon />, onPress: () => setShowMenu(true) },
      ]} />
      <div className="mtabs project-list-tabs">
        {(['active', 'delivered', 'all', 'clean_up'] as const).map(v => <button key={v} type="button" className={`t${view === v ? ' on' : ''}`} aria-pressed={view === v} onClick={() => changeView(v)}>{v === 'active' ? 'Active' : v === 'delivered' ? 'Delivered' : v === 'all' ? 'All' : 'Clean up'}{v !== 'all' && <span className="c">{v === 'active' ? active.length : v === 'delivered' ? delivered.length : cleanupRows.length}</span>}</button>)}
        {savedViews.map(v => <button key={v.id} type="button" className={`t acc${paramsEqual(projectListSaved(mobileState), v.params) ? ' on' : ''}`} onClick={() => applyMobile(projectListFromSaved(v.params))}>★ {v.name}</button>)}
      </div>
      {view !== 'clean_up' && (query || phaseFilter) && <div className="project-list-active-filters chiprow">{query && <button className="c on" onClick={() => setQuery('')}>Search: {query} ×</button>}{phaseFilter && <button className="c on" onClick={clearPhaseFilter}>{PHASE_LABELS[phaseFilter]} ×</button>}</div>}
      <div className="project-list-vitals">{view === 'clean_up' ? <><span className="gold">{cleanupNeedBackup} NEED A BACKUP{cleanupNeedSelects > 0 && ` · ${cleanupNeedSelects} NEED SELECTS`}</span><span>Not counted as active</span></> : view === 'delivered' ? <><span className="accent">{deliveredFY} DELIVERED THIS FY</span><span>Sort · {SORT_OPTIONS.find(o => o.key === sort.key)?.label} {sort.dir === 'asc' ? '↑' : '↓'}</span></> : <><span className="gold">{vitals.next}</span><span>{vitals.counts}</span></>}</div>
      <div className="page-body project-list-body"><div className="project-list-content">
        {!loaded ? <SkeletonRows rows={6} /> : view === 'clean_up' ? <section>
          <div className="project-list-group"><span className="phase-dot pd-cleanup" /><span>Clean up</span><span className="project-list-group-count">{cleanupRows.length}</span></div>
          {cleanupRows.map(p => <div className="project-list-row cleanup-row" key={p.id}>
            <button type="button" className="project-list-open" onClick={() => navigate(`/projects/${p.id}`)} aria-label={`Open ${p.name}`}>
              <span className="prow-main"><span className="prow-title">{p.name}</span>
                <span className="prow-meta">{p.client_name && <><span className="prow-client">{p.client_name}</span><span className="prow-sep">·</span></>}<span>Delivered {formatDayMonth(p.delivered_date)}</span></span>
                <span className="prow-next">{cleanupReason(p)}</span></span>
              <span className="prow-right"><span>{p.project_number !== null ? formatProjectNumber(p.project_number) : '—'}</span></span>
            </button>
            <button type="button" className="project-list-close" onClick={() => setClosingId(p.id)}>Close →</button>
          </div>)}
          {!cleanupRows.length && <div className="project-list-empty">Nothing left to clean up.</div>}
        </section> : view === 'active' ? <>
          {(phaseFilter ? SHELVES.filter(s => s.phase === phaseFilter) : SHELVES).map(shelf => <section key={shelf.phase}>
            <div className="project-list-group"><span className={`phase-dot ${shelf.dot}`} /><span>{PHASE_LABELS[shelf.phase]}</span><span className="project-list-group-count">{byPhase.get(shelf.phase)?.length ?? 0}</span></div>
            {(byPhase.get(shelf.phase) ?? []).map(p => mobileRow(p))}
            {!byPhase.get(shelf.phase)?.length && <div className="project-list-empty">No projects here.</div>}
          </section>)}
          {!phaseFilter && <section><div className="project-list-group"><span className="phase-dot" style={{ background: 'var(--ok)' }} /><span>Recently delivered</span><span className="project-list-group-count">{recent.length}</span></div>
            {recent.map(p => mobileRow(p, true))}{recent.length === 0 && <div className="project-list-empty">Nothing delivered yet.</div>}
            <button className="show-more" onClick={() => changeView('delivered')}>Show all delivered →</button>
          </section>}
        </> : <>{listRows.map(p => mobileRow(p, true))}{!listRows.length && <div className="project-list-empty">Nothing here.</div>}
          {numberedTrash.length > 0 && !showNumberedTrash && <button className="show-more" onClick={() => setShowNumberedTrash(true)}>Show {numberedTrash.length} numbered trash record{numberedTrash.length === 1 ? '' : 's'} →</button>}
          {showNumberedTrash && numberedTrash.map(p => <NumberedTrashProjectRow key={p.id} p={p} />)}
        </>}
      </div></div>
      <PageMenu open={showMenu} title="Projects" onClose={() => setShowMenu(false)} items={[
        { key: 'new', label: 'New project', onSelect: () => setShowNew(true) },
        csvExport.item,
        { key: 'filter', label: 'Filter & sort', onSelect: openFilters },
        { key: 'search', label: 'Search', onSelect: () => window.dispatchEvent(new Event('ss:open-search')) },
      ]} />
      <PageMenu open={!!menuProject} title={menuProject?.name} onClose={() => setRowMenuId(null)} items={rowItems} />
      {phaseProject && <PickerSheet open title={`Move to phase · ${phaseProject.name}`} items={PROJECT_PHASES.map(phase => ({ value: phase, label: PHASE_LABELS[phase] }))} value={phaseProject.phase} onSelect={phase => { void changePhase(phase); }} onClose={() => setPhaseId(null)} />}
      {showFilters && <ProjectListFilterSheet draft={draft} setDraft={setDraft} fields={filterFields} views={savedViews} count={draftCount} onClose={() => setShowFilters(false)} onApply={() => { applyMobile(draft); setShowFilters(false); }} onSave={name => {
        void api('/api/admin/views', { method: 'POST', json: { page: 'projects', name, params: projectListSaved(draft) } })
          .then(() => { loadViews(); refresh(); }).catch(error => reportError(error, 'Could not save the view.'));
      }} />}
      {taskProjectId && <NewTaskModal projects={taskProjects} defaultProjectId={taskProjectId} onCreated={refresh} onClose={() => setTaskProjectId(null)} />}
      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={refresh} />}
      {closeModal}
    </>;
  }

  return (
    <>
      <PageHeader
        title="Projects"
        count={phaseFilter ? filtered.length : showVoided ? projects.length : nonVoidedProjects.length}
      />

      <div className="toolbar">
        <Tabs
          options={[
            { value: 'active', label: 'Active' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'all', label: 'All' },
            { value: 'clean_up', label: 'Clean up', count: cleanupRows.length },
          ]}
          value={view}
          onChange={changeView}
        />
        {view === 'clean_up' ? <span className="toolbar-spacer" /> : <>
        {phaseFilter && (
          <span className="filter-chip" title="Showing only this phase — remove to drag between phases">
            Phase: {PHASE_LABELS[phaseFilter]}
            <span
              className="x"
              role="button"
              tabIndex={0}
              aria-label={`Clear phase filter ${PHASE_LABELS[phaseFilter]}`}
              onClick={clearPhaseFilter}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') clearPhaseFilter();
              }}
            >
              ×
            </span>
          </span>
        )}
        <FilterBar
          fields={filterFields}
          active={filters}
          onChange={(key, value) => {
            clearPhaseFilter();
            setFilters((f) => ({ ...f, [key]: value }));
          }}
        />
        <SortButton options={SORT_OPTIONS} value={sort} onChange={setSort} />
        <SearchBox
          placeholder="Search projects, clients…"
          value={query}
          onChange={(nextQuery) => {
            clearPhaseFilter();
            setQuery(nextQuery);
          }}
        />
        {projects.some((p) => p.voided_at !== null) && (
          <button
            className="btn"
            aria-pressed={showVoided}
            onClick={() => {
              clearPhaseFilter();
              setShowVoided((show) => !show);
            }}
          >
            {showVoided ? 'Hide voided' : 'Show voided'}
          </button>
        )}
        <CsvExportButton state={csvExport} />
        </>}
        <button className="btn primary" onClick={() => setShowNew(true)}>
          + New project
        </button>
      </div>

      {view !== 'clean_up' && <ViewChips
        page="projects"
        current={viewState}
        isDefault={isDefaultView}
        allowSave={!phaseFilter}
        onApply={applyView}
      />}

      <div className="page-body">
        <div className="content wide">
          {view !== 'clean_up' && <VitalsSlim>
            <VS label="Active" value={activeCount} tone="accent" />
            <VS label="Next shoot" value={nextShoot ? formatWeekdayDate(nextShoot) : '—'} tone="gold" />
            <VS label="Open tasks" value={openTasks} />
            <VS label="Delivered this FY" value={deliveredFY} />
          </VitalsSlim>}

          {!loaded ? (
            <SkeletonRows rows={6} />
          ) : view === 'clean_up' ? (
            <>
              <div className="cleanup-intro">
                <span className="phase-dot pd-cleanup" />
                Delivered work still being tidied — selects to export, footage to back up. Not counted as active.
              </div>
              <div className="group" style={{ marginTop: 10 }}>
                <div className="group-head">
                  <span className="group-title">Clean up</span>
                  <span className="group-count">{cleanupRows.length}</span>
                  <span className="group-note">Closes once a backup drive or “no footage” is confirmed</span>
                </div>
                {cleanupRows.map((p) => (
                  <div className="row" key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                    <span className={`num${sort.key === 'number' ? ' hi' : ''}`}>
                      {p.project_number !== null ? formatProjectNumber(p.project_number) : '—'}
                    </span>
                    <span className="row-title" title={p.name}>{p.name}</span>
                    <span className="row-context" title={p.client_name}>{p.client_name}</span>
                    <span className="row-spacer" />
                    {cleanupReason(p)}
                    <BrandBadge brand={p.brand} />
                    <span className="row-meta dim col-due">{formatDayMonth(p.delivered_date)}</span>
                    <button
                      className="btn cleanup-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClosingId(p.id);
                      }}
                    >
                      Close…
                    </button>
                  </div>
                ))}
                {cleanupRows.length === 0 && <div className="empty-note">Nothing left to clean up.</div>}
              </div>
            </>
          ) : view === 'active' ? (
            <>
              <DndContext
                sensors={sensors}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragEnd={onDragEnd}
              >
                {(phaseFilter ? SHELVES.filter((shelf) => shelf.phase === phaseFilter) : SHELVES).map(
                  (shelf) => (
                    <PhaseShelf
                      key={shelf.phase}
                      phase={shelf.phase}
                      dot={shelf.dot}
                      projects={byPhase.get(shelf.phase) ?? []}
                      today={today}
                      draggingId={draggingId}
                      isOver={overShelf === shelf.phase}
                      onOpen={(id) => navigate(`/projects/${id}`)}
                      onSendNow={sendWaitingNow}
                      onSnooze={snoozeWaiting}
                      waitingActionId={waitingActionId}
                      waitingNote={waitingNote}
                    />
                  ),
                )}
                <DragOverlay>
                  {draggingProject && (
                    <div className="project-card dragging" style={{ paddingBottom: 0 }}>
                      <ProjectCardBody p={draggingProject} today={today} />
                    </div>
                  )}
                </DragOverlay>
              </DndContext>

              {!phaseFilter && (
                <div className="shelf">
                  <div className="shelf-head">
                    <span className="phase-dot" style={{ background: 'var(--ok)' }} />
                    <span className="shelf-title">Recently delivered</span>
                    <span className="shelf-count">{deliveredFY} this FY</span>
                  </div>
                  {recentDelivered.map((p, i) => (
                    <div
                      className="row"
                      key={p.id}
                      style={i === 0 ? { marginTop: 4 } : undefined}
                      onClick={() => navigate(`/projects/${p.id}`)}
                    >
                      <span className={`num${sort.key === 'number' ? ' hi' : ''}`}>
                        {p.project_number !== null ? formatProjectNumber(p.project_number) : '—'}
                      </span>
                      <span className="row-title" title={p.name}>{p.name}</span>
                      <span className="row-context" title={p.client_name}>{p.client_name}</span>
                      <span className="row-spacer" />
                      <BrandBadge brand={p.brand} />
                      {p.voided_at !== null && <Pill tone="danger">Voided</Pill>}
                      <span className="row-meta dim col-due">{formatDayMonth(p.delivered_date)}</span>
                    </div>
                  ))}
                  {delivered.length > 3 && (
                    <button className="show-more" onClick={() => changeView('delivered')}>
                      Show all delivered →
                    </button>
                  )}
                  {delivered.length === 0 && <div className="empty-shelf">Nothing delivered yet.</div>}
                </div>
              )}
            </>
          ) : (
            <div className="group" style={{ marginTop: 18 }}>
              <div className="group-head">
                <span className="group-title">{view === 'all' ? 'All projects' : 'Delivered'}</span>
                <span className="group-count">{listRows.length}</span>
              </div>
              {listRows.map((p) => (
                <div className="row" key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                  <span className={`num${sort.key === 'number' ? ' hi' : ''}`}>
                    {p.project_number !== null ? formatProjectNumber(p.project_number) : '—'}
                  </span>
                  <span className="row-title" title={p.name}>{p.name}</span>
                  <span className="row-context" title={p.client_name}>{p.client_name}</span>
                  <span className="row-spacer" />
                  <BrandBadge brand={p.brand} />
                  {p.voided_at !== null && <Pill tone="danger">Voided</Pill>}
                  {view === 'all' && <Pill tone={pillTone(p.phase)}>{PHASE_LABELS[p.phase]}</Pill>}
                  <span className="row-meta dim col-due">
                    {formatDayMonth(p.phase === 'delivered' || p.phase === 'clean_up' ? p.delivered_date : p.due_date)}
                  </span>
                </div>
              ))}
              {listRows.length === 0 && <div className="empty-note">Nothing here.</div>}
              {numberedTrash.length > 0 && !showNumberedTrash && (
                <button className="show-more" onClick={() => setShowNumberedTrash(true)}>
                  Show {numberedTrash.length} numbered trash record{numberedTrash.length === 1 ? '' : 's'} →
                </button>
              )}
              {showNumberedTrash && numberedTrash.map((p) => <NumberedTrashProjectRow key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </div>

      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={refresh} />}
      {closeModal}
    </>
  );
}
